import { createSlice, PayloadAction, combineReducers } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import {
  updateFreeFiles,
  successfullFilePreload,
  updateShippingServices,
} from "../shippingCreate/createShippingSlice";
import {
  ShippingCaseResponse,
  CargoDocs,
  Document,
  ActivitiesResponse,
  ActivityItem,
  CreateOriginFields,
} from "./types";
import { AppThunk, RootState } from "../../store";

import cargoReducer from "./tabs/cargoDetails/cargoSlice";
import insuranceReducer from "./tabs/insurance/insuranceSlice";

import unloadingReducer, {
  actions as unloadingActions,
} from "./tabs/unloading/unloadingSlice";

import packingReducer from "./tabs/packingList/packingSlice";

import deliveryReducer from "./tabs/delivery/deliverySlice";

import tmsReducer from "./tabs/tms/tmsSlice";
import customsReducer from "./tabs/customs/customsSlice";

import {
  getShippingCaseApi,
  getActivitiesApi,
  getActivitiesByRouteApi,
  updateOriginApi,
  completeActivityApi,
} from "./api";
import { routeToTabKeyMap } from "./helpers/tabs-mapping";

import incomeStatementReducer from "./tabs/incomeStatement/incomeStatementSlice";

const handleDataToFiles = (docs: Document[], dispatch: any) => {
  docs.forEach((doc) => {
    const isKnownDoc = Object.keys(CargoDocs).includes(doc.name);
    const payload = {
      doc_type: doc.name,
      ...doc,
    };

    if (isKnownDoc) {
      dispatch(successfullFilePreload(payload));
    } else {
      dispatch(updateFreeFiles({ file: doc }));
    }
  });
};

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type ShippingViewState = {
  shipping: ViewState<ShippingCaseResponse | null> & {
    activities: ActivitiesResponse;
  };
  freight: ViewState<ActivityItem[]>;
  originCharge: ViewState<ActivityItem[]>;
};

export type ShippingViewKey = "shipping" | "originCharge" | "freight";

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: ShippingViewState = {
  shipping: {
    ...getInitialState<ShippingCaseResponse | null>(null),
    activities: null,
    success: null,
    error: null,
  },
  originCharge: getInitialState<ActivityItem[]>([]),
  freight: getInitialState<ActivityItem[]>([]),
};

const shippingViewSlice = createSlice({
  name: "shippingView",
  initialState,
  reducers: {
    getStart(state: ShippingViewState, action: PayloadAction<ShippingViewKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getSuccess(
      state: ShippingViewState,
      action: PayloadAction<{ key: ShippingViewKey; items: any }>,
    ) {
      const { key, items } = action.payload;
      state[key].items = items;
      state[key].loading = false;
      state[key].error = null;
    },
    getShippingSuccess(
      state: ShippingViewState,
      action: PayloadAction<ShippingCaseResponse>,
    ) {
      state.shipping.items = action.payload;
      state.shipping.loading = false;
      state.shipping.error = null;
    },
    getActivitiesSuccess(
      state: ShippingViewState,
      action: PayloadAction<ActivitiesResponse>,
    ) {
      state.shipping.loading = false;
      state.shipping.activities = action.payload;
    },
    updateActivities(
      state: ShippingViewState,
      action: PayloadAction<ActivityItem>,
    ) {
      const current = state.shipping.items?.activities;
      if (!current?.length) return;

      const index = current.findIndex((s) => s.id === action.payload.id);

      if (index !== -1) {
        current[index] = action.payload;
      }
    },
    createSuccess(
      state: ShippingViewState,
      action: PayloadAction<{ key: ShippingViewKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    getFailure(
      state: ShippingViewState,
      action: PayloadAction<{ key: ShippingViewKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state[key].loading = false;
      state[key].error = error;
    },
    cleanData(
      state: ShippingViewState,
      action: PayloadAction<ShippingViewKey>,
    ) {
      state[action.payload].items = null;
    },
    hideWarning(
      state: ShippingViewState,
      action: PayloadAction<ShippingViewKey>,
    ) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
    },
  },
});

export const {
  getStart,
  getSuccess,
  updateActivities,
  getShippingSuccess,
  getActivitiesSuccess,
  getFailure,
  cleanData,
  createSuccess,
  hideWarning,
} = shippingViewSlice.actions;

const shippingViewReducer = combineReducers({
  shipping: shippingViewSlice.reducer,
  cargo: cargoReducer,
  insurance: insuranceReducer,
  unloading: unloadingReducer,
  packing: packingReducer,
  delivery: deliveryReducer,
  customs: customsReducer,
  tms: tmsReducer,
  incomeStatement: incomeStatementReducer,
});

export default shippingViewReducer;

const selectShippingView = (state: RootState) => state.viewShipping;

export const selectShipping = createSelector(
  [selectShippingView],
  (view) => view.shipping,
);
export const getShippingTitle = createSelector(
  [selectShippingView],
  (view) => view.shipping?.shipping?.items?.title ?? "",
);
export const getTypeOfShipment = createSelector(
  [selectShippingView],
  (view) => view.shipping.shipping?.items?.offer?.type_of_shipment,
);
export const getDischargePort = createSelector(
  [selectShippingView],
  (view) => view.shipping.shipping?.items?.offer?.discharge_port,
);
export const getTypeOfPackingGeneration = createSelector(
  [selectShippingView],
  (view) => view.shipping.shipping?.items?.packing_list_generation_type,
);
export const selectCargoView = createSelector(
  [selectShippingView],
  (view) => view.cargo,
);

export const selectInsuranceView = createSelector(
  [selectShippingView],
  (view) => view.insurance,
);

export const selectUnloadingView = createSelector(
  [selectShippingView],
  (view) => view.unloading,
);

export const selectPackingView = createSelector(
  [selectShippingView],
  (view) => view.packing,
);

export const selectDeliveryView = createSelector(
  [selectShippingView],
  (view) => view.delivery,
);

export const selectCustomsView = createSelector(
  [selectShippingView],
  (view) => view.customs,
);

export const selectTmsView = createSelector(
  [(state: RootState) => state.viewShipping],
  (view) => view.tms.tms,
);
export const commoditiesTmsView = createSelector(
  [selectShippingView],
  (view) => view.tms?.packing,
);

export const availableServices = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.activities,
);

export const shipmentType = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.items?.offer.type_of_shipment,
);

export const dischargePort = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.items?.offer.discharge_port,
);
export const assignedOffice = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.items?.offer.assigned_office,
);

export const shippingCustomer = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.items?.offer.customer.id || null,
);

export const shippingCustomerName = createSelector(
  [selectShipping],
  (shipping) => shipping.shipping.items?.offer.customer.name || null,
);

export const fetchShippingCase =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("shipping"));
      const data = await getShippingCaseApi(id);
      dispatch(getShippingSuccess(data));
      handleDataToFiles(data.cargo_docs, dispatch);
      dispatch(updateShippingServices(data.activities));
      dispatch(
        unloadingActions.getSuccess({
          key: "notes",
          items: {
            unloading_special_instruction: data.unloading_special_instruction,
            unloading_note: data.unloading_note,
          },
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "shipping", error }));
    }
  };

export const fetchActivitiesByRoute =
  (id: number, route: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart(routeToTabKeyMap[route] as ShippingViewKey));
      const { items } = await getActivitiesByRouteApi(id, route);
      dispatch(
        getSuccess({
          key: routeToTabKeyMap[route] as ShippingViewKey,
          items,
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(
        getFailure({ key: routeToTabKeyMap[route] as ShippingViewKey, error }),
      );
    }
  };

export const updateOriginCharge =
  (
    params: CreateOriginFields,
    shipping_id: number,
    id: number,
    key: string,
  ): AppThunk =>
  async (dispatch) => {
    try {
      await updateOriginApi(params, shipping_id, id);
      dispatch(
        createSuccess({
          key: "originCharge",
          success: "Origin Charge was updated successfully",
        }),
      );
      dispatch(fetchActivitiesByRoute(shipping_id, key));
    } catch {
      dispatch(
        getFailure({
          key: "originCharge",
          error: "Origin Charge update failed",
        }),
      );
    }
  };

export const updateFreightCharge =
  (
    params: CreateOriginFields,
    shipping_id: number,
    id: number,
    key: string,
  ): AppThunk =>
  async (dispatch) => {
    try {
      await updateOriginApi(params, shipping_id, id);
      dispatch(
        createSuccess({
          key: "freight",
          success: "Freight was updated successfully",
        }),
      );
      dispatch(fetchActivitiesByRoute(shipping_id, key));
    } catch {
      dispatch(
        getFailure({
          key: "freight",
          error: "Freight update failed",
        }),
      );
    }
  };

export const fetchActivities =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("shipping"));
      const items = await getActivitiesApi(id);
      dispatch(getActivitiesSuccess(items));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "shipping", error }));
    }
  };

export const completeActivity =
  (
    sid: number,
    activityId: number,
    params: { completion_date: string; execution_date: string },
  ): AppThunk =>
  async (dispatch) => {
    try {
      const data = await completeActivityApi(sid, activityId, params);
      dispatch(updateActivities(data));
      dispatch(
        createSuccess({
          key: "shipping",
          success: "Activity completed successfully",
        }),
      );
    } catch {
      dispatch(
        getFailure({ key: "shipping", error: "Completing activity failed" }),
      );
    }
  };
